import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Badge,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, IconButton, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Menu, MenuItem
} from '@mui/material';
import {
  Search as SearchIcon, Phone as PhoneIcon, Add as AddIcon,
  Refresh as RefreshIcon, Note as NoteIcon, Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon, HourglassEmpty as PendingIcon,
  CheckCircle as ResolvedIcon, Call as CalledIcon, Block as BlockIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import api from '../services/api';

interface TeleChallanData {
  _id: string; originalChallanId: string; originalUserId: string;
  challanData: { challanId: string; amount: number; paid: boolean; dueDate: string; createdAt: string };
  userData: { fullName: string; rollNumber: string; phone: string; email: string; city: string };
  status: string; assignedDate: string;
  notes?: { text: string; date: string; admin: string; _id: string }[];
}

const STATUS_MAP: any = {
  pending:     { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)',  label: 'PENDING',     icon: <PendingIcon  sx={{ fontSize: 16 }} /> },
  called:      { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: 'CALLED',      icon: <CalledIcon   sx={{ fontSize: 16 }} /> },
  resolved:    { color: '#059669', bg: 'rgba(5,150,105,0.1)', label: 'RESOLVED',    icon: <ResolvedIcon sx={{ fontSize: 16 }} /> },
  unreachable: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  label: 'UNREACHABLE', icon: <BlockIcon    sx={{ fontSize: 16 }} /> },
};

const MiniStat = ({ label, val, color, bg, icon }: any) => (
  <Card sx={{ flex: '1 1 160px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)', overflow: 'hidden', transition: 'transform .2s', '&:hover': { transform: 'translateY(-3px)' } }}>
    <Box sx={{ height: 3, background: `linear-gradient(90deg,${color},#fbbf24)` }} />
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.4 }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>{val}</Typography>
        </Box>
        <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const TeleChallan = () => {
  const [teleChallans, setTeleChallans]   = useState<TeleChallanData[]>([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(10);
  const [totalCount, setTotalCount]       = useState(0);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [addLoading, setAddLoading]       = useState(false);
  const [addResult, setAddResult]         = useState<{ added: number; skipped: number } | null>(null);
  const [daysPreset, setDaysPreset]       = useState<string>('30');
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [openNotesDialog, setOpenNotesDialog]   = useState(false);
  const [selectedChallan, setSelectedChallan]   = useState<TeleChallanData | null>(null);
  const [newNote, setNewNote]             = useState('');
  const [noteLoading, setNoteLoading]     = useState(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading]       = useState(false);

  // For status change menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeChallanId, setActiveChallanId] = useState<string | null>(null);

  const fetchTeleChallans = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/tele-challans', {
        params: { page: page + 1, limit: rowsPerPage, search, status: statusFilter }
      });
      if (res.data.success) {
        setTeleChallans(res.data.data.teleChallans);
        setTotalCount(res.data.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch tele-marketing list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeleChallans(); }, [page, rowsPerPage, search, statusFilter]);

  const handleAddChallans = async () => {
    setAddLoading(true); setAddResult(null);
    try {
      const payload: any = (startDate || endDate)
        ? { ...(startDate && { startDate }), ...(endDate && { endDate }) }
        : { days: daysPreset };
      const res = await api.post('/tele-challans/add', payload);
      if (res.data.success) {
        setAddResult(res.data.data);
        fetchTeleChallans();
        setSuccess(res.data.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add unpaid challans');
    } finally {
      setAddLoading(false);
    }
  };

  const handleOpenNotes = (challan: TeleChallanData) => { setSelectedChallan(challan); setNewNote(''); setOpenNotesDialog(true); };

  const handleAddNote = async () => {
    if (!selectedChallan || !newNote.trim()) return;
    setNoteLoading(true);
    try {
      const res = await api.put(`/tele-challans/${selectedChallan._id}/notes`, { note: newNote });
      if (res.data.success) {
        const updated = res.data.data;
        setTeleChallans(prev => prev.map(c => c._id === updated._id ? updated : c));
        setSelectedChallan(updated); setNewNote('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add note');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, challanId: string) => {
    setStatusMenuAnchor(event.currentTarget);
    setActiveChallanId(challanId);
  };

  const handleStatusClose = () => {
    setStatusMenuAnchor(null);
    setActiveChallanId(null);
  };

  const handleStatusUpdate = async (status: string) => {
    if (!activeChallanId) return;
    try {
      const res = await api.put(`/tele-challans/${activeChallanId}/status`, { status });
      if (res.data.success) {
        const updated = res.data.data;
        setTeleChallans(prev => prev.map(c => c._id === updated._id ? updated : c));
        setSuccess('Status updated successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      handleStatusClose();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this Tele Challan?')) return;
    try {
      const res = await api.delete(`/tele-challans/${id}`);
      if (res.data.success) { setSuccess('Deleted successfully'); fetchTeleChallans(); }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleDeleteAll = async () => {
    setDeleteAllLoading(true);
    try {
      const res = await api.delete('/tele-challans/delete/all');
      if (res.data.success) { setSuccess(res.data.message); setOpenDeleteAllDialog(false); fetchTeleChallans(); }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete all data');
    } finally {
      setDeleteAllLoading(false);
    }
  };

  // Stats from active loaded list
  const pending     = teleChallans.filter(c => c.status === 'pending').length;
  const called      = teleChallans.filter(c => c.status === 'called').length;
  const resolved    = teleChallans.filter(c => c.status === 'resolved').length;
  const unreachable = teleChallans.filter(c => c.status === 'unreachable').length;

  const chartData = [
    { name: 'Pending',     count: pending,     color: '#fbbf24' },
    { name: 'Called',      count: called,      color: '#10b981' },
    { name: 'Resolved',    count: resolved,    color: '#059669' },
    { name: 'Unreachable', count: unreachable, color: '#ef4444' },
  ];

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#059669' }}>Telemarketing & Calls</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, mt: 0.25 }}>Manage unpaid challan follow-up calls and status updates</Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}
            sx={{ borderRadius: '12px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none', fontWeight: 500 }}>
            Add Unpaid Challans
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={() => setOpenDeleteAllDialog(true)}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 500 }}>
            Clear List
          </Button>
        </Box>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: '14px' }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '14px' }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stat Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <MiniStat label="Pending Calls" val={pending} color="#fbbf24" bg="rgba(251,191,36,0.1)" icon={<PendingIcon sx={{ fontSize: 20 }} />} />
        <MiniStat label="Called Student" val={called} color="#10b981" bg="rgba(16,185,129,0.1)" icon={<CalledIcon sx={{ fontSize: 20 }} />} />
        <MiniStat label="Resolved Payments" val={resolved} color="#059669" bg="rgba(5,150,105,0.1)" icon={<ResolvedIcon sx={{ fontSize: 20 }} />} />
        <MiniStat label="Unreachable Student" val={unreachable} color="#ef4444" bg="rgba(239,68,68,0.1)" icon={<DeleteIcon sx={{ fontSize: 20 }} />} />
      </Box>

      {/* Chart + Filters */}
      <Box display="flex" gap={2.5} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 280px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#059669', mb: 2 }}>Status Overview</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <RTooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '0.82rem' }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card sx={{ flex: '1 1 500px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <SearchIcon sx={{ color: '#059669', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Search & Filter</Typography>
            </Box>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" width="100%">
              <TextField fullWidth size="small" label="Search User / Challan / Phone" value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ endAdornment: <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /> }}
                sx={{ flex: { xs: '1 1 auto', sm: '2 1 0px' }, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
              <TextField select fullWidth size="small" label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }} 
                InputLabelProps={{ shrink: true }}
                sx={{ flex: { xs: '1 1 auto', sm: '1 1 0px' }, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="called">Called</option>
                <option value="resolved">Resolved</option>
                <option value="unreachable">Unreachable</option>
              </TextField>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTeleChallans}
                sx={{ 
                  borderRadius: '12px', 
                  textTransform: 'none', 
                  fontWeight: 600, 
                  color: '#059669', 
                  borderColor: '#059669',
                  height: '40px',
                  whiteSpace: 'nowrap',
                  px: 3,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { bgcolor: 'rgba(5,150,105,0.05)', borderColor: '#047857' }
                }}>
                Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['User', 'Contact', 'Challan ID', 'Amount', 'Payment', 'Due Date', 'Status', 'Assigned', 'Notes', 'Del'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress size={36} sx={{ color: '#059669' }} /></TableCell></TableRow>
              ) : teleChallans.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 500 }}>No records found. Click "Add Unpaid Challans" to start.</TableCell></TableRow>
              ) : teleChallans.map(item => {
                const sc = STATUS_MAP[item.status] || STATUS_MAP['pending'];
                return (
                  <TableRow key={item._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: '#059669', fontSize: '0.75rem', fontWeight: 700 }}>
                          {item.userData.fullName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, color: '#1e293b' }}>{item.userData.fullName}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {item.userData.rollNumber ? (item.userData.rollNumber.startsWith("DS") ? "HS" + item.userData.rollNumber.slice(2) : item.userData.rollNumber) : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.8rem', color: '#334155', fontWeight: 500 }}>{item.userData.phone}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', fontFamily: 'monospace' }}>{item.challanData.challanId}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669' }}>Rs. {item.challanData.amount.toLocaleString()}</Typography></TableCell>
                    <TableCell>
                      <Chip label={item.challanData.paid ? 'Paid' : 'Unpaid'} size="small"
                        sx={{ fontWeight: 700, fontSize: '0.68rem', bgcolor: item.challanData.paid ? 'rgba(5,150,105,0.1)' : 'rgba(239,68,68,0.1)', color: item.challanData.paid ? '#059669' : '#dc2626' }} />
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{item.challanData.dueDate ? new Date(item.challanData.dueDate).toLocaleDateString() : 'N/A'}</Typography></TableCell>
                    <TableCell>
                      <Chip 
                        icon={sc.icon} 
                        label={sc.label} 
                        size="small"
                        onClick={(e) => handleStatusClick(e, item._id)}
                        onDelete={(e) => handleStatusClick(e, item._id)}
                        deleteIcon={<ArrowDropDownIcon sx={{ '&&': { color: sc.color } }} />}
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: '0.68rem', 
                          bgcolor: sc.bg, 
                          color: sc.color, 
                          cursor: 'pointer',
                          '& .MuiChip-icon': { color: sc.color },
                          '&:hover': { opacity: 0.85 }
                        }} 
                      />
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{new Date(item.assignedDate).toLocaleDateString()}</Typography></TableCell>
                    <TableCell>
                      <Badge badgeContent={item.notes?.length || 0} color="success">
                        <IconButton size="small" onClick={() => handleOpenNotes(item)} sx={{ bgcolor: 'rgba(5,150,105,0.08)', borderRadius: '8px' }}>
                          <NoteIcon sx={{ fontSize: 17, color: '#059669' }} />
                        </IconButton>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item._id)} sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: '8px' }}>
                        <DeleteIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination component="div" count={totalCount} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      </Card>

      {/* Quick Status Update Menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={handleStatusClose} PaperProps={{ sx: { borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' } }}>
        <MenuItem onClick={() => handleStatusUpdate('pending')} sx={{ gap: 1, fontSize: '0.8rem', fontWeight: 600, color: '#fbbf24' }}>
          <PendingIcon sx={{ fontSize: 16 }} /> PENDING
        </MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('called')} sx={{ gap: 1, fontSize: '0.8rem', fontWeight: 600, color: '#10b981' }}>
          <CalledIcon sx={{ fontSize: 16 }} /> CALLED
        </MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('resolved')} sx={{ gap: 1, fontSize: '0.8rem', fontWeight: 600, color: '#059669' }}>
          <ResolvedIcon sx={{ fontSize: 16 }} /> RESOLVED
        </MenuItem>
        <MenuItem onClick={() => handleStatusUpdate('unreachable')} sx={{ gap: 1, fontSize: '0.8rem', fontWeight: 600, color: '#ef4444' }}>
          <BlockIcon sx={{ fontSize: 16 }} /> UNREACHABLE
        </MenuItem>
      </Menu>

      {/* Add Dialog */}
      <Dialog open={openAddDialog} onClose={() => !addLoading && setOpenAddDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: '#059669' }}>Add Unpaid Challans</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#334155', mb: 1 }}>Select Timeframe</Typography>
            <Box display="flex" gap={1} mb={3}>
              {[7, 30, 60].map(days => (
                <Button key={days} variant={daysPreset === String(days) && !startDate ? 'contained' : 'outlined'}
                  onClick={() => { setDaysPreset(String(days)); setStartDate(''); setEndDate(''); }}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                  Last {days} Days
                </Button>
              ))}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#334155', mb: 1 }}>Or Custom Range</Typography>
            <Box display="flex" gap={2}>
              <TextField fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate}
                onChange={e => { setStartDate(e.target.value); setDaysPreset(''); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
              <TextField fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate}
                onChange={e => { setEndDate(e.target.value); setDaysPreset(''); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
            </Box>
            {addResult && <Alert severity="success" sx={{ mt: 2, borderRadius: '12px' }}>Added: <b>{addResult.added}</b>, Skipped: <b>{addResult.skipped}</b></Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenAddDialog(false)} disabled={addLoading} sx={{ borderRadius: '10px' }}>Close</Button>
          <Button onClick={handleAddChallans} variant="contained" disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            sx={{ borderRadius: '10px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none', fontWeight: 500 }}>
            {addLoading ? 'Adding...' : 'Add to List'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={openNotesDialog} onClose={() => setOpenNotesDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: '#059669' }}>
          Notes — {selectedChallan?.userData.fullName}
          <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 400, mt: 0.25 }}>Challan ID: {selectedChallan?.challanData.challanId}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 260, overflowY: 'auto', bgcolor: '#f8fafc', borderRadius: '12px', p: 2, mb: 2 }}>
            {selectedChallan?.notes?.length ? selectedChallan.notes.map((note, index) => (
              <Card key={index} sx={{ mb: 1.5, borderRadius: '12px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>{note.text}</Typography>
                  <Box display="flex" justifyContent="space-between" mt={0.75}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>By: {note.admin || 'Admin'}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{new Date(note.date).toLocaleString()}</Typography>
                  </Box>
                </CardContent>
              </Card>
            )) : <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center' }}>No notes yet.</Typography>}
          </Box>
          <TextField fullWidth multiline rows={3} placeholder="Type a new note..." value={newNote} onChange={e => setNewNote(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenNotesDialog(false)} sx={{ borderRadius: '10px' }}>Close</Button>
          <Button onClick={handleAddNote} variant="contained" disabled={!newNote.trim() || noteLoading}
            sx={{ borderRadius: '10px', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none', fontWeight: 500 }}>
            {noteLoading ? 'Saving...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Dialog */}
      <Dialog open={openDeleteAllDialog} onClose={() => !deleteAllLoading && setOpenDeleteAllDialog(false)} PaperProps={{ sx: { borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.05)' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: '#059669' }}>Delete All Data?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: '12px' }}>This action cannot be undone. All Tele Challan records will be permanently deleted.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenDeleteAllDialog(false)} disabled={deleteAllLoading} sx={{ borderRadius: '10px' }}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained" disabled={deleteAllLoading}
            startIcon={deleteAllLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 500 }}>
            {deleteAllLoading ? 'Deleting...' : 'Yes, Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeleChallan;
